import React, { useState, useEffect, useCallback } from "react";
import { securePost, secureGet } from "../../config/api";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { formatDateTime } from "../../utils/format";

import Filter from "../../components/Filter";
import PageScroller from "../../components/PageScroller";
import Rows from "../../components/Rows";
import SnackbarNotification from '../../components/SnackbarNotification';

import LoadingLogo from "../../components/LoadingLogo";
import arrowIcon from "../../icons/chevron-rightP.svg";


const Transfers = () => {
  const [, setAllTransfersData] = useState([]);
  const [, setFilteredData] = useState([]);
  const [currentPageData, setCurrentPageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    stockSource: "",
    stockDestination: "",
    product: "",
  });
  const [repos, setRepos] = useState([]);
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { t } = useTranslation();
  
  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const columns = [
    { label: t(`columns.id`), key: "id" },
    { label: "Date", key: "createdAt", render: (date) => formatDateTime(date) },
    { label: t(`columns.sourceRepo`), key: "sourceRepo" },
    {
      label: "",
      key: "arrowIcon",
      render: () => (
        <div className="flex items-center justify-center">
          <img src={arrowIcon} alt="Arrow" className="w-5 h-5" />
          </div>
      ),
    },
    { label: t(`columns.destRepo`), key: "destRepo" },
    {
      label: t(`columns.articleCount`),
      key: "itemCount",
      render: (_, row) => row.productQuantities?.length || 0,
    },
    {
      label: t(`columns.totalQuantity`),
      key: "totalQuantity",
      render: (_, row) =>
        row.productQuantities?.reduce((sum, product) => sum + (product.quantity || 0), 0) || 0,
    },
  ];

  const filterOptions = [
    { name: "dateRange", type: "daterange" },
    {
      name: "stockSource",
      label: t(`filters.transfers.sourceRepo`),
      type: "dropdown",
      options: repos.map((repo) => ({ value: repo.id, label: repo.name })),
    },
    {
      name: "stockDestination",
      label: t(`filters.transfers.destRepo`),
      type: "dropdown",
      options: repos.map((repo) => ({ value: repo.id, label: repo.name })),
    },
    {
      name: "product",
      label: t(`filters.transfers.product`),
      type: "dropdown",
      options: products.map((product) => ({
        value: product.id,
        reference: product.reference,
        name: product.name,
      }))
    },
  ];

  
  const fetchTransfersData = useCallback(async (page, size, signal) => {
    setLoading(true);
    const requestBody = {
      startDate: filters.startDate || null,
      endDate: filters.endDate || null,
      destinationRepo: filters.stockDestination || null,
      productId: filters.product ? parseInt(filters.product, 10) : null,
      sourceRepo: filters.stockSource || null,
    };


    try {
      const response = await securePost(`/repos/transfers/mine/grouped/paged/web?page=${page}&size=${size}`, requestBody, showSnackbar, { signal });

      if (response && response.data) {
        const { content, totalPages } = response.data;
        setAllTransfersData(content);
        setFilteredData(content);
        setTotalPages(totalPages);
        setCurrentPageData(content);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Error fetching transfers data:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchTransfersData(currentPage, pageSize, abortController.signal);
    return () => {
      abortController.abort();
    };
  }, [fetchTransfersData, currentPage, pageSize]);

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    const fetchDropdownOptions = async () => {
      try {
        const reposResponse = await secureGet("/repos", showSnackbar, { signal });
        setRepos(reposResponse.data);

        const fetchedProducts = await secureGet("/products/unpaginated", showSnackbar, { signal });
        setProducts(fetchedProducts.data);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Error fetching dropdown options:", error);
        }
      }
    };

    fetchDropdownOptions();

    return () => {
      abortController.abort();
    };
  }, []);


  const handleFilterChange = (filterName, filterValue) => {
    const updatedFilters = { ...filters };

    if (filterName === "dateRange") {
      const { startDate, endDate } = filterValue || {};
      updatedFilters.startDate = startDate || "";
      updatedFilters.endDate = endDate || "";
    } else {
      updatedFilters[filterName] = filterValue;
    }

    setFilters(updatedFilters);
    setCurrentPage(0);
  };



  const resetFilters = () => {
    setFilters({
      startDate: "",
      endDate: "",
      stockSource: "",
      stockDestination: "",
      product: "",
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
  };


  return (
    <div>
      <Helmet>
        <title>GrayFox - {t("titles.transfers")}</title>
      </Helmet>
      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t(`titles.transfers`)}</h1>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={(filterName, filterValue) => handleFilterChange(filterName, filterValue)}
        resetFilters={resetFilters}
        filterValues={filters}
      />

      <div className="table-container" style={{ position: "relative", minHeight: "200px" }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              //backgroundColor: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1,
            }}
          >
            <LoadingLogo />
          </div>
        )}
        {!loading && (
          <Rows
            columns={columns}
            data={currentPageData}
            expandable={true}
          />
        )}
      </div>

      <PageScroller
        pageNumber={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );
};

export default Transfers;
